/**
 * Componente de mapa e links de localização
 */

import type { EstablishmentPublic } from "../types";
import {
  generateGoogleMapsLink,
  generateWazeLink,
  formatAddress,
  normalizeMapCoordinate,
} from "../utils";
import { FiMapPin } from "react-icons/fi";
import { SiGooglemaps, SiWaze } from "react-icons/si";

interface LocationInfoProps {
  establishment: EstablishmentPublic;
  showMap?: boolean;
}

export function LocationInfo({ establishment, showMap = true }: LocationInfoProps) {
  const mapLat = normalizeMapCoordinate(establishment.mapLat);
  const mapLng = normalizeMapCoordinate(establishment.mapLng);
  const hasCoordinates = mapLat !== null && mapLng !== null;
  const hasAddress = establishment.addressLine || establishment.city;
  if (!hasCoordinates && !hasAddress) return null;

  if (showMap && hasCoordinates) {
    const googleMapsUrl = generateGoogleMapsLink(mapLat, mapLng);
    const wazeUrl = generateWazeLink(mapLat, mapLng);

    return (
      <div className="mx-auto mt-4 max-w-6xl px-4">
        <div
          className="relative overflow-hidden rounded-3xl border border-slate-200/80 bg-white shadow-sm shadow-slate-200/70"
          style={{ height: 260 }}
        >
          <iframe
            src={`https://www.google.com/maps?q=${encodeURIComponent(
              `${mapLat},${mapLng}`
            )}&z=16&output=embed`}
            width="100%"
            height="100%"
            style={{ border: 0 }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          {googleMapsUrl && (
            <a
              href={googleMapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex h-10 items-center gap-2 rounded-xl bg-[#4285F4] px-3 text-sm font-bold text-white shadow-sm transition hover:bg-[#357AE8] focus:outline-none focus-visible:ring-4 focus-visible:ring-sky-100"
            >
              <SiGooglemaps /> <span>Abrir no Google Maps</span>
            </a>
          )}

          {wazeUrl && (
            <a
              href={wazeUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex h-10 items-center gap-2 rounded-xl bg-[#33CCFF] px-3 text-sm font-bold text-[#1C2330] shadow-sm transition hover:bg-[#2EBBE6] focus:outline-none focus-visible:ring-4 focus-visible:ring-sky-100"
            >
              <SiWaze /> <span>Abrir no Waze</span>
            </a>
          )}
        </div>
      </div>
    );
  }

  // Sem coordenadas, mas tem endereço: mostrar apenas links
  const address = formatAddress(establishment);
  if (!address) return null;

  return (
    <section
      aria-label="Endereço do estabelecimento"
      data-testid="location-info"
      className="mx-auto mt-3 max-w-6xl px-4"
    >
      <div className="box-border rounded-2xl border border-slate-200/80 bg-white p-3 shadow-sm shadow-slate-200/70">
        <div className="flex min-w-0 items-start gap-2 text-sm text-slate-600">
          <FiMapPin className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary-600" />
          <span className="min-w-0 break-words font-medium">{address}</span>
        </div>
      </div>
    </section>
  );
}
